import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, FormFieldMessage, Input, Select } from '@/components/ui';
import type { RoleKeyRow } from '@/api/console';
import type { OrgLevel } from '@/api/orgAdmin';

export interface CreateLevelFormState {
    name: string;
    role_key: string;
    description: string;
}

export interface CreateLevelFieldsProps {
    form: CreateLevelFormState;
    setForm: (next: CreateLevelFormState) => void;
    /** The governed standard-role vocabulary — `GET /api/v1/role-keys`. */
    roleKeys: RoleKeyRow[];
    roleKeysLoading: boolean;
    /** The vocabulary could not be read (offline, or no permission for it). */
    roleKeysFailed: boolean;
    /** This org's existing Levels — used to warn about a duplicate `role_key`. */
    activeLevels: OrgLevel[];
    error: string | null;
}

/**
 * EAA-1817: the three fields of "Thêm level", extracted from
 * `LevelAssignmentModals` so the standard-role field can carry real behaviour.
 *
 * WHY `role_key` IS NO LONGER FREE TEXT. A Level is the assignable half of a
 * pair; `/console/role-map` owns the other half, and the two meet on this
 * string. Typed by hand it silently fails closed: a Level whose key matches no
 * standard role provisions nothing into any application, and nothing in the UI
 * ever says so — the Level looks created because it WAS created. Choosing from
 * the vocabulary removes the failure mode rather than validating after the fact.
 *
 * THE FREE-TEXT INPUT IS STILL THE FALLBACK, DELIBERATELY. `GET /role-keys` is
 * a separate endpoint and a separate permission; if it errors or returns
 * nothing, a Select over an empty list would make the form impossible to
 * submit. So an unreadable vocabulary degrades to the old input plus a hint
 * saying the key was not verified — worse, but not blocked.
 *
 * PICKING A ROLE FILLS AN EMPTY NAME. Every standard role already has a display
 * name, and it is nearly always the Level's name too ("Teacher" -> "Teacher").
 * It fills only when the field is untouched, so it never overwrites a name the
 * user has chosen.
 */
export function CreateLevelFields({
    form,
    setForm,
    roleKeys,
    roleKeysLoading,
    roleKeysFailed,
    activeLevels,
    error,
}: CreateLevelFieldsProps) {
    const { t } = useTranslation();

    const options = useMemo(
        () =>
            roleKeys
                .filter((row) => row.is_active)
                .map((row) => ({
                    value: row.key,
                    label: row.name || row.key,
                    // Searching by key matters as much as by name: the key is what
                    // the role map, the CSV export and the API all show.
                    title: row.key,
                })),
        [roleKeys]
    );

    // A Level already using this key is not an error — an org may want two
    // Levels resolving to the same app roles — but it is nearly always a
    // mistake, so say it before the write rather than after.
    const duplicate = useMemo(
        () => activeLevels.find((level) => level.role_key === form.role_key),
        [activeLevels, form.role_key]
    );

    const useFreeText = roleKeysFailed || (!roleKeysLoading && options.length === 0);

    function handleRoleKeyChange(nextKey: string) {
        const picked = roleKeys.find((row) => row.key === nextKey);
        setForm({
            ...form,
            role_key: nextKey,
            name: form.name.trim() ? form.name : picked?.name || form.name,
        });
    }

    let roleKeyControl = (
        <Select
            showSearch
            value={form.role_key || undefined}
            onChange={handleRoleKeyChange}
            options={options}
            loading={roleKeysLoading}
            optionFilterProp="label"
            filterOption={(input, option) =>
                `${option?.label ?? ''} ${option?.title ?? ''}`
                    .toLowerCase()
                    .includes(input.toLowerCase())
            }
            placeholder={t('console.hierarchy.levelAssignmentModals.roleKeySelectPlaceholder')}
            style={{ width: '100%' }}
        />
    );
    if (useFreeText) {
        roleKeyControl = (
            <Input
                type="text"
                placeholder={t('console.hierarchy.levelAssignmentModals.roleKeyPlaceholder')}
                value={form.role_key}
                onChange={(e) => setForm({ ...form, role_key: e.target.value })}
            />
        );
    }

    let roleKeyHint = t('console.hierarchy.levelAssignmentModals.roleKeyHint');
    if (useFreeText) {
        roleKeyHint = t('console.hierarchy.levelAssignmentModals.roleKeyHintUnverified');
    } else if (form.role_key) {
        roleKeyHint = t('console.hierarchy.levelAssignmentModals.roleKeyHintChosen', {
            key: form.role_key,
        });
    }

    return (
        <>
            <FormField
                label={t('console.hierarchy.levelAssignmentModals.nameLabel')}
                required
                hint={t('console.hierarchy.levelAssignmentModals.nameHint')}
            >
                <Input
                    type="text"
                    placeholder={t('console.hierarchy.levelAssignmentModals.namePlaceholder')}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
            </FormField>

            <FormField
                label={t('console.hierarchy.levelAssignmentModals.roleKeyLabel')}
                required
                hint={roleKeyHint}
            >
                {roleKeyControl}
            </FormField>

            {duplicate && (
                <FormFieldMessage
                    hint={t('console.hierarchy.levelAssignmentModals.roleKeyDuplicate', {
                        name: duplicate.name,
                    })}
                />
            )}

            <FormField
                label={t('console.hierarchy.levelAssignmentModals.descriptionLabel')}
                hint={t('console.hierarchy.levelAssignmentModals.descriptionHint')}
            >
                <Input
                    type="text"
                    placeholder={t(
                        'console.hierarchy.levelAssignmentModals.descriptionPlaceholder'
                    )}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
            </FormField>

            {error && <FormFieldMessage error={error} />}
        </>
    );
}

export default CreateLevelFields;
